import { useEffect } from 'react';
import type { TradeType } from '@/types';
import type { Counterparty } from '@/types/counterparty.types';
import type { TradeForm } from '../../components/trade-form-sections/types';

const COUNTERPARTY_ACCOUNT_SUFFIX: Record<TradeType, string> = {
  lend: '001',
  borrow: '002',
  repo: '003',
  reverse_repo: '004',
};

/**
 * Derive counterparty_account as `{clientId}.{suffix}` from the chosen
 * counterparty and trade type. Extracted verbatim from useCreateTradeForm
 * (LR-495); the trade_type coercion it used to carry went with LR-634, since a
 * counterparty may hold both master agreements and every leg books on it.
 */
export function useCounterpartyAccountSync({
  form,
  counterpartyName,
  tradeType,
  counterparties,
}: {
  form: TradeForm;
  counterpartyName: string;
  tradeType: string;
  counterparties: Counterparty[];
}) {
  useEffect(() => {
    if (!counterpartyName || !tradeType || !counterparties.length) return;

    const cp = counterparties.find((c) => c.name === counterpartyName);
    if (!cp) return;

    const account = `${cp.clientId}.${COUNTERPARTY_ACCOUNT_SUFFIX[tradeType as TradeType]}`;
    form.setFieldValue('counterparty_account', account);
  }, [counterpartyName, tradeType, counterparties, form]);
}

/**
 * Write the computed read-only custodian_account into the form (LR-439). The
 * value is fully derived by Custodian × Trade Type × Settlement Venue upstream;
 * this effect just mirrors it into the field. Extracted from useCreateTradeForm (LR-495).
 */
export function useCustodianAccountSync({
  form,
  computedCustodianAccount,
}: {
  form: TradeForm;
  computedCustodianAccount: string;
}) {
  useEffect(() => {
    form.setFieldValue('custodian_account', computedCustodianAccount);
  }, [computedCustodianAccount, form]);
}
